import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { usePagination } from "../hooks/usePagination";

const useDebouncedValue = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
};

const PaginatedBucketList = ({
  title,
  buckets,
  defaultBucket,

  fetchItems,
  fetchCounts,

  baseFilters = {},
  enabled = true,

  renderItem,
  getItemKey = (item) => item.id,

  pageSize = 20,

  searchPlaceholder = "Search...",
  searchFilterName = "search",
  debounceMs = 400,
  showSearch = true,

  refreshKey = 0,
  pollInterval = 10000,

  emptyMessage = "No results in this category.",
}) => {
  const [selectedBucket, setSelectedBucket] = useState(defaultBucket);

  const [searchValue, setSearchValue] = useState("");

  const debouncedSearch = useDebouncedValue(searchValue, debounceMs);

  const emptyCounts = useMemo(
    () => Object.fromEntries(buckets.map((bucket) => [bucket.countKey, 0])),
    [buckets],
  );

  const [counts, setCounts] = useState(emptyCounts);

  const filters = useMemo(
    () => ({
      ...baseFilters,
      bucket: selectedBucket,
      ...(debouncedSearch
        ? {
            [searchFilterName]: debouncedSearch,
          }
        : {}),
    }),
    [baseFilters, selectedBucket, debouncedSearch, searchFilterName],
  );

  const {
    options: items,
    loading,
    error,
    page,
    pageInfo,
    handlePageChange,
    refetch,
  } = usePagination(
    ({ first, after }) =>
      fetchItems({
        first,
        after,
        ...filters,
      }),
    pageSize,
    filters,
    enabled,
  );

  const loadCounts = useCallback(async () => {
    if (!enabled || !fetchCounts) {
      return;
    }

    try {
      /*
       * Search is deliberately excluded here.
       *
       * Counts represent the overall totals for each
       * bucket, rather than totals for the current search.
       */
      const response = await fetchCounts(baseFilters);

      setCounts(response || emptyCounts);
    } catch (error) {
      console.error("Failed to fetch bucket counts", error);
    }
  }, [enabled, fetchCounts, baseFilters, emptyCounts]);

  /*
   * Keep references to the latest versions of these
   * functions without making the polling interval depend
   * on their function identities.
   */
  const refetchRef = useRef(refetch);
  const loadCountsRef = useRef(loadCounts);

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    loadCountsRef.current = loadCounts;
  }, [loadCounts]);

  /*
   * Fetch counts initially and whenever the scope changes.
   */
  useEffect(() => {
    loadCountsRef.current();
  }, [enabled, baseFilters]);

  /*
   * Poll without depending on refetch/loadCounts.
   *
   * Inline fetch functions and pagination updates can
   * change those function identities frequently. Using
   * refs prevents the interval being constantly reset.
   */
  useEffect(() => {
    if (!pollInterval || !enabled) {
      return;
    }

    const interval = setInterval(() => {
      refetchRef.current({
        silent: true,
      });

      loadCountsRef.current();
    }, pollInterval);

    return () => clearInterval(interval);
  }, [pollInterval, enabled]);

  /*
   * Allow a parent component to force an immediate
   * refresh, e.g. after creating a new upload.
   */
  useEffect(() => {
    if (refreshKey > 0) {
      refetchRef.current();
      loadCountsRef.current();
    }
  }, [refreshKey]);

  const handleBucketChange = (_, bucket) => {
    setSelectedBucket(bucket);
  };

  return (
    <Box sx={{ mt: 4 }}>
      {title && (
        <Typography variant="h5" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}

      <Card>
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "stretch",
              md: "center",
            },
            flexDirection: {
              xs: "column",
              md: "row",
            },
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Tabs
            value={selectedBucket}
            onChange={handleBucketChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            {buckets.map((bucket) => (
              <Tab
                key={bucket.value}
                value={bucket.value}
                label={`${bucket.label} (${counts[bucket.countKey] ?? 0})`}
              />
            ))}
          </Tabs>

          {showSearch && (
            <TextField
              size="small"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder={searchPlaceholder}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                width: {
                  xs: "auto",
                  md: 280,
                },
                m: 1,
              }}
            />
          )}
        </Box>

        <CardContent>
          {loading && items.length > 0 && <LinearProgress sx={{ mb: 2 }} />}

          {loading && items.length === 0 ? (
            <Stack alignItems="center" sx={{ py: 5 }}>
              <CircularProgress size={32} />
            </Stack>
          ) : error ? (
            <Typography color="error" align="center" sx={{ py: 4 }}>
              Failed to load results.
            </Typography>
          ) : items.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              {typeof emptyMessage === "function"
                ? emptyMessage(selectedBucket, Boolean(debouncedSearch))
                : emptyMessage}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {items.map((item) => (
                <React.Fragment key={getItemKey(item)}>
                  {renderItem(item, selectedBucket, {
                    refetch: () => refetchRef.current(),
                    refreshCounts: () => loadCountsRef.current(),
                  })}
                </React.Fragment>
              ))}
            </Stack>
          )}

          {(page > 1 || pageInfo.hasNextPage) && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mt: 3,
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Button
                disabled={page <= 1 || loading}
                onClick={() => handlePageChange(null, page - 1)}
              >
                Previous
              </Button>

              <Typography variant="body2" color="text.secondary">
                Page {page}
              </Typography>

              <Button
                disabled={!pageInfo.hasNextPage || loading}
                onClick={() => handlePageChange(null, page + 1)}
              >
                Next
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaginatedBucketList;
